import { useCallback } from "react";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import {
  IInventoryItemSearchResultModel,
  IInventoryTransferFormModel,
  IInventoryTransferLineFormModel,
  searchResultToFormLine,
} from "../models/inventoryTransferModels";

/**
 * Wraps `useFieldArray` for the transfer dialog's `lines` collection and
 * exposes ergonomic add/update/remove helpers. Duplicates are allowed — the
 * caller intentionally never dedupes because a single product can appear on
 * multiple lines with different expiry batches.
 */
export const useInventoryTransferLines = (
  form: UseFormReturn<IInventoryTransferFormModel>,
  disabled: boolean,
) => {
  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: "lines",
  });

  const addLineFromProduct = useCallback(
    (product: IInventoryItemSearchResultModel) => {
      if (disabled) return;
      append(searchResultToFormLine(product));
    },
    [append, disabled],
  );

  const updateQuantity = useCallback(
    (index: number, quantity: number) => {
      if (disabled) return;
      const current = form.getValues(`lines.${index}`);
      update(index, { ...current, quantity });
    },
    [disabled, form, update],
  );

  const updateExpiration = useCallback(
    (index: number, date: Date | null) => {
      if (disabled) return;
      const current = form.getValues(`lines.${index}`);
      update(index, { ...current, expirationDate: date });
    },
    [disabled, form, update],
  );

  const removeLine = useCallback(
    (index: number) => {
      if (disabled) return;
      remove(index);
    },
    [disabled, remove],
  );

  return {
    lines: fields as unknown as IInventoryTransferLineFormModel[],
    addLineFromProduct,
    updateQuantity,
    updateExpiration,
    removeLine,
  };
};
